import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * Top-nav "About" dropdown menu — replaces the standalone About NavLink in each
 * public site's main nav. Submenu items: About Us / Leadership Message /
 * Executive Body Members / Governing Body Members.
 *
 * Props:
 *   linkClass({isActive}) — same className function used by sibling NavLinks
 *                           so the trigger matches the rest of the nav.
 *   testidPrefix          — e.g. "poly-nav-about" / "bul-nav-about" / "roe-nav-about"
 *   menuClassName         — Tailwind classes for the popover panel (theme).
 */
export default function AboutMenu({
  linkClass,
  testidPrefix = "nav-about",
  menuClassName = "bg-white text-slate-800 ring-1 ring-slate-200",
  itemHoverClassName = "hover:bg-slate-50",
  items,
  triggerLabel = "About Us",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const loc = useLocation();

  // Auto-close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const defaultItems = [
    { to: "about", label: "About Us" },
    { to: "vision-mission", label: "Vision & Mission" },
    { to: "leadership", label: "Leadership Message" },
    { to: "executive-body", label: "Executive Body Members" },
    { to: "governing-body", label: "Governing Body Members" },
  ];
  const menuItems = Array.isArray(items) && items.length > 0 ? items : defaultItems;

  // The trigger should appear "active" when the user is on any About sub-route.
  const slugSegment = loc.pathname.split("/").filter(Boolean)[1] || "";
  const activeSlugs = menuItems.map((i) => i.to);
  const isAboutActive = activeSlugs.includes(slugSegment);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`${testidPrefix}-root`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={linkClass({ isActive: isAboutActive }) + " inline-flex items-center gap-1"}
        data-testid={`${testidPrefix}-trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {triggerLabel}
        <ChevronDown size={12} strokeWidth={2.4} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`${testidPrefix}-menu`}
          className={`absolute left-0 top-full pt-1 min-w-[240px] z-50`}
        >
          <div className={`rounded-sm shadow-lg py-1 ${menuClassName}`}>
            {menuItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "about"}
                onClick={() => setOpen(false)}
                data-testid={`${testidPrefix}-item-${it.to}`}
                className={({ isActive }) =>
                  `block px-4 py-2 text-sm font-medium ${itemHoverClassName} ${
                    isActive ? "opacity-100 font-semibold" : "opacity-90"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
